import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { OrderProposalToolbar } from './order-proposal-toolbar'
import { OrderProposalTable } from './order-proposal-table'
import { FillBar } from './fill-bar'
import type { TableColumn } from './order-proposal-table'
import type {
  ColumnMode,
  DateColumn,
} from '@/features/reports/lib/order-proposal-math'
import type { OrderProposalPrefs } from '@/features/reports/store/order-proposal-store'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useLocationStore } from '@/shared/store/location-store'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { useOrderProposalQuery } from '@/features/reports/api/use-order-proposal-query'
import {
  buildProposalView,
  effectiveSort,
  fillOrders,
  isSingleDay,
  mainCategories,
  nextSort,
  nextWeekday,
  resolveColumn,
  takenQuantity,
} from '@/features/reports/lib/order-proposal-math'
import { orderProposalCsvLines } from '@/features/reports/lib/order-proposal-csv'
import { formatColumn } from '@/features/reports/lib/order-proposal-labels'
import { downloadCsv } from '@/features/reports/lib/export-csv'
import { reportDate } from '@/features/reports/lib/report-zone'
import { accountingUserKey } from '@/features/reports/store/accounting-ansicht-store'
import {
  prefsOf,
  useOrderProposalStore,
} from '@/features/reports/store/order-proposal-store'
import { useOrderProposalSessionStore } from '@/features/reports/store/order-proposal-session-store'
import { SendPurchaseOrderButton } from '@/features/purchase-orders/components/send-purchase-order-button'

/**
 * Bestellvorschlag — what sold, what is there, what to order (CHG-2026-10-bestellvorschlag).
 *
 * One Standort and its order days (a Liefertag or a Liefertag range), laid out like the manager's Excel: stock, incoming and Gesamt per
 * article, then per date column the sales and "sell the same again". The Bestellung typed here
 * lives in memory only (D9) — kept for the session, so it survives opening the draft that
 * "Bestellung senden" creates from it (CHG-2026-10-edi-purchase-orders, D12).
 */
export function OrderProposalPage() {
  return (
    <PermissionGate action={ACTIONS.REPORT_ORDER_PROPOSAL_VIEW}>
      <OrderProposalContent />
    </PermissionGate>
  )
}

function OrderProposalContent() {
  const { t } = useTranslation('reports')
  const { can } = usePermissions()
  const canOrder = can(ACTIONS.REPORT_ORDER_PROPOSAL_ORDER)
  const today = useMemo(() => reportDate(new Date()), [])

  // Every Standort, as in Buchhaltung: the report is for one explicitly chosen store (D13). The
  // shared report location seeds it.
  const { data: restaurantData } = useRestaurants()
  const restaurants = restaurantData?.items ?? []
  const storedIds = useLocationStore((s) => s.restaurantIds)
  const restaurantId = useOrderProposalSessionStore((s) => s.restaurantId)
  const setRestaurantId = useOrderProposalSessionStore((s) => s.setRestaurantId)
  useEffect(() => {
    if (restaurantId != null || restaurants.length === 0) return
    const seeded = restaurants.find((r) => r.id === storedIds[0])
    setRestaurantId(seeded?.id ?? restaurants[0].id)
  }, [restaurantId, restaurants, storedIds, setRestaurantId])

  // The order days. Not remembered across reloads: the report opens on the next weekday (D6).
  const storedDelivery = useOrderProposalSessionStore((s) => s.delivery)
  const setDelivery = useOrderProposalSessionStore((s) => s.setDelivery)
  const delivery = useMemo<DateColumn>(() => {
    if (storedDelivery) return storedDelivery
    const day = nextWeekday(today)
    return { from: day, to: day }
  }, [storedDelivery, today])

  const userKey = accountingUserKey(useAuthStore((s) => s.user))
  const byUser = useOrderProposalStore((s) => s.byUser)
  const setPrefs = useOrderProposalStore((s) => s.setPrefs)
  const prefs = prefsOf(byUser, userKey)
  const update = (next: Partial<OrderProposalPrefs>) => setPrefs(userKey, next)

  // One date column (CHG-2026-10-bestellvorschlag-simplify, D1); the report keeps its column list.
  const modes: Array<ColumnMode> = [prefs.column1]
  const columns: Array<TableColumn> = modes.map((mode) => ({
    mode,
    column: resolveColumn(mode, today, delivery),
  }))

  const report = useOrderProposalQuery(
    restaurantId == null
      ? null
      : {
          restaurantId,
          delivery,
          columns: columns.map((c) => c.column),
        },
  )
  const data = report.data
  const isRange = !isSingleDay(columns[0].column)
  const sort = effectiveSort(prefs.sort, columns[0].column)

  const categories = useMemo(() => (data ? mainCategories(data) : []), [data])
  // A remembered Hauptgruppe the Standort does not have would empty the list for no visible reason.
  const mainCategoryId =
    prefs.mainCategoryId != null &&
    categories.some((c) => c.id === prefs.mainCategoryId)
      ? prefs.mainCategoryId
      : null

  const orders = useOrderProposalSessionStore((s) => s.orders)
  const updateOrders = useOrderProposalSessionStore((s) => s.setOrders)
  const setOrders = (
    next:
      | Record<string, string>
      | ((current: Record<string, string>) => Record<string, string>),
  ) => updateOrders(typeof next === 'function' ? next : () => next)

  const view = useMemo(
    () =>
      data
        ? buildProposalView(data, {
            mainCategoryId,
            showTagesgerichte: prefs.showTagesgerichte,
            hideZeroRows: prefs.hideZeroRows,
            orders,
            sort,
          })
        : null,
    [
      data,
      mainCategoryId,
      prefs.showTagesgerichte,
      prefs.hideZeroRows,
      orders,
      sort,
    ],
  )

  const taken = useOrderProposalSessionStore((s) => s.taken)
  const setTaken = useOrderProposalSessionStore((s) => s.setTaken)
  // A Bestellung changed any other way than by taking a cell is no longer that cell's.
  const untake = (keys: Array<string>) =>
    setTaken((current) => {
      const next = { ...current }
      for (const key of keys) delete next[key]
      return next
    })
  const [soldOpen, setSoldOpen] = useState(false)

  const isEmpty =
    data != null &&
    data.articles.length === 0 &&
    data.tagesgerichte.planned.length === 0 &&
    data.tagesgerichte.sold.length === 0
  const message = report.isError
    ? t('errorDescription')
    : isEmpty
      ? t('orderProposal.empty')
      : null

  const restaurantName =
    restaurants.find((r) => r.id === restaurantId)?.name ?? ''
  const mainCategoryName =
    categories.find((c) => c.id === mainCategoryId)?.name ?? null

  const handleCsv = () => {
    if (!view) return
    downloadCsv(
      orderProposalCsvLines(
        t,
        view,
        {
          restaurantName,
          delivery,
          columns: columns.map((c) => c.column),
          mainCategoryName,
          showTagesgerichte: prefs.showTagesgerichte,
          hideZeroRows: prefs.hideZeroRows,
        },
        canOrder ? orders : null,
      ),
      `${t('orderProposal.csvPrefix')}-${delivery.from}.csv`,
    )
  }

  return (
    // On screen the page fills the main area and only the table scrolls, so the top stays put
    // (CHG-2026-10-bestellvorschlag-simplify, D3); on paper it flows as before.
    <div className="flex h-full min-h-0 flex-col gap-4 print:block print:h-auto print:space-y-4">
      <div className="flex shrink-0 flex-col gap-3">
        <h1 className="text-2xl font-bold">{t('orderProposal.title')}</h1>
        {/* On paper the toolbar is gone; this line keeps what it said. */}
        <p className="hidden text-sm print:block">
          {[
            restaurantName,
            `${t('orderProposal.deliveryDay')} ${formatColumn(delivery, true)}`,
            `${t('orderProposal.mainCategory')} ${mainCategoryName ?? t('orderProposal.allMainCategories')}`,
            `${t('orderProposal.tagesgerichte')} ${prefs.showTagesgerichte ? t('orderProposal.on') : t('orderProposal.off')}`,
            `${t('orderProposal.hideZeroRows')} ${prefs.hideZeroRows ? t('orderProposal.on') : t('orderProposal.off')}`,
          ].join(' · ')}
        </p>
        <OrderProposalToolbar
          restaurants={restaurants}
          restaurantId={restaurantId}
          onRestaurantChange={(id) => {
            if (id === restaurantId) return
            setRestaurantId(id)
            // Another store's Bestellung is not this one's (D9).
            setOrders({})
            setTaken(() => ({}))
          }}
          delivery={delivery}
          onDeliveryChange={setDelivery}
          mainCategories={categories}
          mainCategoryId={mainCategoryId}
          onMainCategoryChange={(id) => update({ mainCategoryId: id })}
          showTagesgerichte={prefs.showTagesgerichte}
          onShowTagesgerichteChange={(show) =>
            update({ showTagesgerichte: show })
          }
          hideZeroRows={prefs.hideZeroRows}
          onHideZeroRowsChange={(hide) => update({ hideZeroRows: hide })}
          onCsv={handleCsv}
          csvDisabled={!view}
          actions={
            canOrder && restaurantId != null ? (
              <SendPurchaseOrderButton
                restaurantId={restaurantId}
                deliveryDate={delivery.from}
                orders={orders}
              />
            ) : null
          }
        />
      </div>

      {canOrder ? (
        <FillBar
          source={prefs.fillSource}
          onSourceChange={(fillSource) => update({ fillSource })}
          upliftPercent={prefs.upliftPercent}
          onUpliftChange={(upliftPercent) => update({ upliftPercent })}
          isRange={isRange}
          onFill={() => {
            if (!view) return
            const source = isRange ? prefs.fillSource : 'sum'
            const filled = fillOrders(view, source, prefs.upliftPercent)
            setOrders((current) => ({ ...current, ...filled }))
            untake(Object.keys(filled))
          }}
          onClear={() => {
            setOrders({})
            setTaken(() => ({}))
          }}
        />
      ) : null}

      <OrderProposalTable
        columns={columns}
        onColumnChange={(_index, mode) => update({ column1: mode })}
        sort={sort}
        onSort={(key) => update({ sort: nextSort(sort, key) })}
        view={view}
        loading={report.isLoading || restaurantData == null}
        message={message}
        canOrder={canOrder}
        orders={orders}
        onOrderChange={(key, value) => {
          setOrders((current) => ({ ...current, [key]: value }))
          untake([key])
        }}
        taken={taken}
        onTake={(key, cell, value) => {
          setOrders((current) => ({ ...current, [key]: takenQuantity(value) }))
          setTaken((current) => ({ ...current, [key]: cell }))
        }}
        soldOpen={soldOpen}
        onSoldOpenChange={setSoldOpen}
      />
    </div>
  )
}
